import { ImageLoaderProps } from 'next/image';
import { useState } from 'react';
import { Button } from '../../../../../components/Clickables/Buttons/Button';
import { FishingReportDownloadModal } from '../../../../../components/FishingReportsGrid/components/FishingReportDownloadModal';
import { Heading } from '../../../../../components/Heading/Heading';
import { FishCatchesIcon } from '../../../../../components/Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../../../../components/NextImage/NextImageWrapper';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { abbreviateNumber } from '@lib/numbers/abbreviateNumber';
import { getLoggedCatchesText } from '../../../helpers/speciesHelpers';
import styles from '../HeaderSection.module.css';

interface IProps {
  catchCount: number;
  imageHeight: number;
  imageUrl: string;
  imageWidth: number;
  isNewCatch: boolean;
  numberOfNewCatches: number;
  postId: string;
  speciesId: string;
  speciesName?: string;
}

export const HeaderImage = ({
  catchCount,
  imageHeight,
  imageWidth,
  imageUrl,
  isNewCatch,
  numberOfNewCatches,
  postId,
  speciesId,
  speciesName,
}: IProps) => {
  const { currentPage } = useAnalytics();

  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const handleImageClick = (): void => {
    setIsAppDownloadModalOpen(true);
    trackEvent({
      name: 'mykiss_card_clicked',
      properties: {
        page: currentPage,
        source: 'HeaderImage',
        source_id: speciesId,
        target: 'catch',
        target_id: postId,
      },
    });
  };

  const imageLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

  return (
    <>
      <Button variant={'textNone'} onClick={handleImageClick} className={styles.catchImageWrapper}>
        <NextImageWrapper
          wrapperClassName="absolute inset-0 size-full"
          imageClassName="size-full"
          customPlaceholderIcon={<FishCatchesIcon size={100} title="fish catches icon" />}
          alt={`The most recent ${speciesName} catches on Fishbrain`}
          src={imageUrl}
          loader={imageLoader}
          fill
          sizes="(min-width: 1680px) 638px,(min-width: 990px) 41.67vw, 100vw" // todo
        />
        <div
          className={`${styles.catchImageOverlay} absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[linear-gradient(0deg,_rgba(9,12,24,0.60)_0%,_rgba(9,12,24,0.60)_100%)] p-2 sm:p-4`}
        >
          <Heading as="span" className="text-sea-foam m-0 text-center" variant="xs">
            {getLoggedCatchesText(catchCount)}
          </Heading>
          {numberOfNewCatches > 0 && (
            <Heading
              as="span"
              variant="2xs"
              className="bg-clownfish-shade m-0 rounded-sm px-2 py-1 text-white"
            >
              {`${abbreviateNumber(numberOfNewCatches)} new`}
            </Heading>
          )}
        </div>
      </Button>
      <FishingReportDownloadModal
        imgProxyLoader={imageLoader}
        isAppDownloadModalOpen={isAppDownloadModalOpen}
        setIsAppDownloadModalOpen={setIsAppDownloadModalOpen}
        sourceId={postId}
        imageUrl={imageUrl}
        isNewCatch={isNewCatch}
      />
    </>
  );
};
